import React from "react";
import { MdDateRange, MdAccessTime } from "react-icons/md";
import { useSettings } from "../context/SettingsContext.jsx";

// Single source of truth for "how do we show a date/time" across the app.
// Reads the user's timezone/date-format/time-format preference from
// SettingsContext (same record Header.jsx already reads) and falls back to
// the browser's locale/timezone until settings load or for logged-out views.
const FALLBACK_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function resolveDatePrefs(settingsRecord) {
  return {
    timeZone: settingsRecord?.timezone?.iana_name || FALLBACK_TZ,
    // Optional short code from the timezone directory (e.g. "IST"); see getTimeZoneLabel
    timeZoneAbbr: settingsRecord?.timezone?.abbreviation?.trim() || "",
    dateFormat: settingsRecord?.date_format || "DD/MM/YYYY",
    timeFormat: settingsRecord?.time_format || "24",
  };
}

// Breaks a JS Date into its calendar fields *as seen in the given timezone*,
// e.g. { year, month, day, hour, minute, second, weekday }. Use this instead
// of Date getters (which are always browser-local) whenever a value needs to
// respect the user's timezone preference.
export function getTzParts(date, timeZone = FALLBACK_TZ) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  }).formatToParts(date);
  return parts.reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
}

// "DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "DD-MM-YYYY" -> "Wed, 11/07/2026"
export function formatDateByPref(date, { timeZone, dateFormat } = {}) {
  const p = getTzParts(date, timeZone);
  switch (dateFormat) {
    case "MM/DD/YYYY":
      return `${p.weekday}, ${p.month}/${p.day}/${p.year}`;
    case "YYYY-MM-DD":
      return `${p.weekday}, ${p.year}-${p.month}-${p.day}`;
    case "DD-MM-YYYY":
      return `${p.weekday}, ${p.day}-${p.month}-${p.year}`;
    case "DD/MM/YYYY":
    default:
      return `${p.weekday}, ${p.day}/${p.month}/${p.year}`;
  }
}

// Respects the user's 12h/24h preference -> "02:30:11 PM" or "14:30:11"
export function formatTimeByPref(date, { timeZone, timeFormat } = {}, { seconds = true } = {}) {
  const p = getTzParts(date, timeZone);
  const time12 = () => {
    let hour12 = Number(p.hour) % 12;
    if (hour12 === 0) hour12 = 12;
    const suffix = Number(p.hour) < 12 ? "AM" : "PM";
    const base = `${String(hour12).padStart(2, "0")}:${p.minute}`;
    return seconds ? `${base}:${p.second} ${suffix}` : `${base} ${suffix}`;
  };
  const time24 = () => {
    const base = `${p.hour}:${p.minute}`;
    return seconds ? `${base}:${p.second}` : base;
  };
  return timeFormat === "12" ? time12() : time24();
}

// Short timezone label for `date`, e.g. "IST", "EST" / "EDT", "GMT+5:30".
// Intl's own code wins when it is a real letter code, because it follows DST
// (EST in winter, EDT in summer) while the directory's stored abbreviation is
// fixed. Intl only has letter codes for a few regions (Asia/Kolkata comes back
// as "GMT+5:30"), so the stored abbreviation is used next, then Intl's offset.
export function getTimeZoneLabel(date, { timeZone = FALLBACK_TZ, timeZoneAbbr } = {}) {
  const intlName =
    new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName")?.value || "";
  if (/^[A-Z]{2,5}$/.test(intlName)) return intlName;
  return timeZoneAbbr || intlName || timeZone;
}

export function getGreeting(timeZone = FALLBACK_TZ) {
  const hour = Number(getTzParts(new Date(), timeZone).hour);
  if (hour < 12) return "Good Morning";
  if (hour < 18) return "Good Afternoon";
  return "Good Evening";
}

// "Just now" / "5m ago" / "3h ago" / "2d ago" / falls back to a locale date
export function getRelativeTime(timestamp) {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return past.toLocaleDateString();
}

// Real UTC instant for a given wall-clock date/time *as seen in `timeZone`* —
// e.g. for a client 12 hours ahead of UTC, "18 July, 09:00" isn't 09:00 UTC,
// it's 12 hours earlier. Intl has no zoned-time-to-UTC conversion, so this
// uses the standard two-pass trick: format a guess instant through the
// target zone to read its offset, then correct for it (a second pass handles
// the rare case where the DST offset itself changes within that lookup).
export function zonedDateTimeToUtc(year, month, day, hour, minute, timeZone) {
  const wallClockAsUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  const offsetMsAt = (instantMs) => {
    const p = getTzParts(new Date(instantMs), timeZone);
    const sameWallClockAsUtcMs = Date.UTC(
      Number(p.year), Number(p.month) - 1, Number(p.day),
      Number(p.hour), Number(p.minute), Number(p.second)
    );
    return sameWallClockAsUtcMs - instantMs;
  };

  let utcMs = wallClockAsUtcMs - offsetMsAt(wallClockAsUtcMs);
  utcMs = wallClockAsUtcMs - offsetMsAt(utcMs);
  return new Date(utcMs);
}

function zonedStartOfDayToUtc(year, month, day, timeZone) {
  return zonedDateTimeToUtc(year, month, day, 0, 0, timeZone);
}

// Countdown label for a date-only deadline (`@db.Date`, e.g. project
// deadline), evaluated in a specific IANA timezone — pass the CLIENT's
// assigned timezone here, not the viewing admin's, so "18 July" means the
// full 18 July calendar day in the client's zone regardless of who's
// looking. Before that day starts: "N days/hours/minutes left" counting
// down to 18 July 00:00. During that day itself: "N hour(s)/minute(s) left
// today" counting down to the start of 19 July. Once 19 July begins: "Due".
// Falls back to the browser's local timezone if none is passed.
// Returns null for an empty/invalid deadline, otherwise
// { label: "3 days left" | "20 hours left" | "10 minutes left"
//        | "4 hours left today" | "Due",
//   urgency: "normal" | "warning" | "danger" | "overdue" }
export function getDeadlineCountdown(deadlineValue, timeZone = FALLBACK_TZ) {
  if (!deadlineValue) return null;
  const deadline = new Date(deadlineValue);
  if (Number.isNaN(deadline.getTime())) return null;

  const year = deadline.getUTCFullYear();
  const month = deadline.getUTCMonth() + 1;
  const day = deadline.getUTCDate();

  const startOfDayUtc = zonedStartOfDayToUtc(year, month, day, timeZone);
  // Date.UTC normalizes day = day + 1 across month/year boundaries for us.
  const startOfNextDayUtc = zonedStartOfDayToUtc(year, month, day + 1, timeZone);

  const now = Date.now();

  if (now >= startOfNextDayUtc.getTime()) return { label: "Due", urgency: "overdue" };

  if (now >= startOfDayUtc.getTime()) {
    const diffMs = startOfNextDayUtc.getTime() - now;
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    if (hours >= 1) {
      return { label: `${hours} hour${hours > 1 ? "s" : ""} left today`, urgency: "danger" };
    }
    const minutes = totalMinutes < 1 ? 1 : totalMinutes;
    return { label: `${minutes} minute${minutes > 1 ? "s" : ""} left today`, urgency: "danger" };
  }

  const diffMs = startOfDayUtc.getTime() - now;
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  if (days >= 1) {
    return { label: `${days} day${days > 1 ? "s" : ""} left`, urgency: days === 1 ? "warning" : "normal" };
  }

  const hours = Math.floor(totalMinutes / 60);
  if (hours >= 1) {
    return { label: `${hours} hour${hours > 1 ? "s" : ""} left`, urgency: hours <= 6 ? "danger" : "warning" };
  }

  const minutes = totalMinutes < 1 ? 1 : totalMinutes;
  return { label: `${minutes} minute${minutes > 1 ? "s" : ""} left`, urgency: "danger" };
}

// Drop-in, timezone/format-aware date+time renderer used across 100+ pages.
// Props are unchanged from the original component, so existing call sites
// (`<LocalDateTime utcTime={...} type="date" is_icon />`) keep working as-is
// — only the underlying formatting now follows the user's Settings
// (timezone / date format / 12h-24h) instead of just the browser's timezone.
export default function LocalDateTime({
  utcTime,
  type = "datetime", // "date" | "time" | "datetime"
  is_icon = false,
  seconds = false,
}) {
  // Settings aren't available outside an authenticated SettingsProvider tree
  // (e.g. public/login pages) — useSettings() returns undefined there via
  // useContext, so we fall back to browser locale in that case.
  const settingsCtx = useSettings();
  const prefs = resolveDatePrefs(settingsCtx?.settings);
  
// console.log('prefs is ', prefs)
  if (!utcTime) return null;

  const date = new Date(utcTime);
  if (Number.isNaN(date.getTime())) return null;

  // Date-only DB columns (`@db.Date`, e.g. project deadline) carry no real
  // time-of-day and are always serialized as exact UTC midnight
  // ("2026-07-21T00:00:00.000Z"). Re-interpreting that instant through the
  // viewer's timezone can roll the calendar day backward/forward even though
  // there's nothing to convert. Real timestamps (created_date, member_since,
  // etc.) always carry a genuine time-of-day, so this only misfires on the
  // ~1-in-86.4-million timestamp that happens to land exactly on UTC
  // midnight — an acceptable tradeoff to avoid a separate prop on every
  // call site.
  const isPureDate =
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;

  let label;
  if (type === "date") {
    label = formatDateByPref(date, isPureDate ? { ...prefs, timeZone: "UTC" } : prefs);
  } else if (type === "time") {
    label = formatTimeByPref(date, prefs, { seconds });
  } else {
    label = `${formatDateByPref(date, prefs)}, ${formatTimeByPref(date, prefs, { seconds })}`;
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 text-inherit tabular-nums"
      title={type === "date" && isPureDate ? date.toUTCString() : date.toLocaleString()}
    >
      {is_icon && type === "date" && (
        <MdDateRange size={13} className="text-text_muted flex-shrink-0" />
      )}
      {is_icon && (type === "time" || type === "datetime") && (
        <MdAccessTime size={13} className="text-text_muted flex-shrink-0" />
      )}
      <span>{label}</span>
    </span>
  );
}
